import { Button } from '@ipfabric/ui-components/Button/Button';
import { renderCondition } from '@ipfabric/utils/rbac/dataPolicyCondition';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo, useState } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { addNewMenuItem } from '../shared/addNewMenuItem';
import { formatGuardedCounts } from '../shared/formatGuardedCounts';
import { policyDeletionMessage } from '../shared/policyDeletionMessage';
import { useConfirmedDelete } from '../shared/useConfirmedDelete';
import { useWriteScopes } from '../shared/useWriteScopes';
import type { DataPolicyFormMode } from './DataPolicyForm/DataPolicyForm';
import {
  type DataPolicy,
  useDataPoliciesApi,
  useDataPoliciesImpactQuery,
  useDataPoliciesQuery,
} from './dataPoliciesApi';
import {
  type DataPoliciesTableRow,
  createDataPoliciesTableColumns,
} from './dataPoliciesTableColumns';

export type OpenedDataPolicy = {
  mode: DataPolicyFormMode;
  policy?: DataPolicy;
};

export const useDataPoliciesTable = () => {
  const snapshotId = useSnapshotId();
  const [opened, setOpened] = useState<OpenedDataPolicy | null>(null);
  const scopes = useWriteScopes('/data-policies', '/data-policies/:id');
  const [managingLabels, setManagingLabels] = useState(false);

  const policiesQuery = useDataPoliciesQuery();
  const impactQuery = useDataPoliciesImpactQuery(snapshotId);
  const { deleteDataPolicy } = useDataPoliciesApi();

  const { confirmDeleteDialog, requestDelete } = useConfirmedDelete<DataPolicy>(
    {
      header: 'Data policy will be deleted',
      message: (item) =>
        policyDeletionMessage('data', item.name, item.assignedRoles),
      deleteItem: (item) => deleteDataPolicy(item.id),
      successMessage: 'Data policy deleted',
    },
  );

  const columns = useMemo(
    () =>
      createDataPoliciesTableColumns({
        scopes,
        onOpen: (policy, mode) => setOpened({ policy, mode }),
        onDelete: (policy) => void requestDelete(policy),
      }),
    [requestDelete, scopes],
  );

  const rows = useMemo<DataPoliciesTableRow[]>(() => {
    const impactById = new Map(
      (impactQuery.data ?? []).map((entry) => [entry.policyId, entry.counts]),
    );
    return (policiesQuery.data ?? []).map((policy) => ({
      id: policy.id,
      name: policy.name,
      condition: renderCondition(policy.condition),
      impact: snapshotId ? formatGuardedCounts(impactById.get(policy.id)) : '—',
      isSystem: policy.isSystem,
      policy,
    }));
  }, [policiesQuery.data, impactQuery.data, snapshotId]);

  const frontendSimpleFilter = useFrontendLocalTable({ columns, data: rows });

  const menuItems: TableActionsItem[] = [
    {
      standalone: true,
      elementWithKey: (
        <Button
          dataTestId='data-policies-manage-enrolled-labels'
          disabled={snapshotId === null}
          key='manageEnrolledLabels'
          icon='fa-list-check'
          onClick={() => setManagingLabels(true)}
          size='small'
          type='button'
          variant='outline'
        >
          Manage enrolled labels
        </Button>
      ),
    },
    ...(scopes.canCreate
      ? [
          addNewMenuItem('data-policies-add-new', () =>
            setOpened({ mode: 'add' }),
          ),
        ]
      : []),
  ];

  return {
    columns,
    confirmDeleteDialog,
    frontendSimpleFilter,
    managingLabels,
    menuItems,
    opened,
    policiesQuery,
    setManagingLabels,
    setOpened,
  };
};
